/**
 * LUME Analytics Module
 * Handles PostHog product analytics and Meta Pixel ad tracking
 *
 * Two-phase initialization for GDPR compliance:
 * Phase 1 (immediate): PostHog in anonymous mode (persistence: 'memory')
 *   - No cookies or localStorage written — fully GDPR-compliant
 *   - Captures pageviews and custom events (quiz funnel)
 *   - Anonymous session recording with aggressive PII masking (legitimate interest)
 *   - No autocapture
 *   - User identity doesn't persist across page loads, but persists within SPA session
 *
 * Phase 2 (after consent): Upgrade to full mode
 *   - Unmasked session recording
 *   - Meta Pixel initialized
 *   - On next page load, returning consented users get localStorage persistence
 */

import posthog from 'posthog-js';

// Environment variables
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const CONSENT_KEY = 'lume-consent';

let isInitialized = false;
let isFullMode = false;

// ============ TRAFFIC SOURCE ============
let _trafficSource = null;

function captureTrafficSource() {
  if (_trafficSource) return _trafficSource;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    utmKeys.forEach(key => {
      const val = params.get(key);
      if (val) source[key] = val;
    });
    if (document.referrer) source.referrer = document.referrer;
    _trafficSource = Object.keys(source).length > 0 ? source : null;
  } catch (e) {
    _trafficSource = null;
  }
  return _trafficSource;
}

export function getTrafficSource() {
  return _trafficSource;
}

// ============ CONSENT ============

export function getConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

export function setConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage unavailable
  }
}

export function hasFullConsent() {
  return getConsent() === 'all';
}

// ============ META PIXEL (consent-required) ============

function initMetaPixel() {
  if (META_PIXEL_ID && typeof window !== 'undefined') {
    if (!window.fbq) {
      (function(f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
      console.log('✅ Meta Pixel initialized');
    }
  } else if (!META_PIXEL_ID) {
    console.warn('⚠️ Meta Pixel ID not configured (REACT_APP_META_PIXEL_ID)');
  }
}

// ============ POSTHOG INITIALIZATION ============

/**
 * Phase 1: Anonymous analytics — call immediately on page load, no consent needed.
 * Uses memory persistence (no cookies/localStorage written to user's device).
 * Events are captured and sent to PostHog but user identity resets on page reload.
 * Within a single SPA session (e.g., ad click → quiz → results), all events share
 * the same anonymous distinct_id.
 */
export function initAnonymousAnalytics() {
  if (isInitialized) return;

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'memory',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-mask="true"], input, textarea',
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          number: true,
        },
        recordCrossOriginIframes: false,
      },
    });
    isInitialized = true;
    console.log('✅ PostHog initialized (anonymous mode — no cookies, masked recording)');

    captureTrafficSource();
    if (_trafficSource && window.posthog) {
      window.posthog.register(_trafficSource);
    }
  } else {
    console.warn('⚠️ PostHog key not configured (REACT_APP_POSTHOG_KEY)');
  }
}

/**
 * Phase 2: Upgrade to full analytics after user clicks "Godta" (Accept).
 * Enables session recording and initializes Meta Pixel.
 * Persistence stays as 'memory' for the current session (can't change after init),
 * but on next page load initAnalytics() will use localStorage directly.
 */
export function upgradeToFullAnalytics() {
  if (!isInitialized) {
    // Not yet initialized — do full init directly
    initAnalytics();
    return;
  }
  if (isFullMode) return;

  // Enable session recording (was disabled in anonymous mode)
  if (POSTHOG_KEY) {
    try {
      posthog.startSessionRecording();
    } catch (e) {
      // Recording may not be available in all environments
    }
  }

  // Initialize Meta Pixel (always requires consent)
  initMetaPixel();

  isFullMode = true;
  console.log('✅ Analytics upgraded to full mode (session recording + Meta Pixel)');
}

/**
 * Full initialization for returning users who already consented.
 * Uses localStorage persistence, session recording, autocapture, and Meta Pixel.
 */
export function initAnalytics() {
  if (isInitialized && isFullMode) return;

  // If already in anonymous mode, just upgrade
  if (isInitialized && !isFullMode) {
    upgradeToFullAnalytics();
    return;
  }

  // Fresh full initialization (returning user with existing consent)
  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'localStorage',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
    });
    console.log('✅ PostHog initialized (full mode — localStorage, consented user)');
  } else {
    console.warn('⚠️ PostHog key not configured (REACT_APP_POSTHOG_KEY)');
  }

  // Initialize Meta Pixel
  initMetaPixel();

  isInitialized = true;
  isFullMode = true;

  captureTrafficSource();
  if (_trafficSource && window.posthog) {
    window.posthog.register(_trafficSource);
  }
}

// Meta standard event name mapping
const META_EVENT_MAP = {
  'product_viewed': 'ViewContent',
  'add_to_cart': 'AddToCart',
  'checkout_started': 'InitiateCheckout',
  'quiz_completed': 'CompleteRegistration',
  'quiz_started': 'Lead',
  'results_viewed': 'ViewContent',
  'recommendation_shown': 'ViewContent',
  'checkout_redirect': 'InitiateCheckout',
  // NOTE: Lead for quiz_started is revisitable — may change based on Meta optimization data
  // Phase 2: Purchase — requires Shopify checkout integration or CAPI (see PHASE2_PURCHASE.md)
};

/**
 * Transform internal event properties to Meta's expected parameter format.
 * Meta has strict parameter names for standard events.
 */
function getMetaParams(metaEvent, properties) {
  switch (metaEvent) {
    case 'ViewContent':
      return {
        content_ids: [properties.product_id || properties.bundle_name].filter(Boolean),
        content_name: properties.product_name || properties.bundle_name,
        content_type: properties.content_type || 'product',
        value: properties.price || properties.total_price,
        currency: properties.currency || 'NOK',
      };
    case 'AddToCart':
      return {
        content_ids: properties.contents
          ? properties.contents.map(c => c.id)
          : [properties.product_id].filter(Boolean),
        content_name: properties.product_name || properties.bundle_name,
        content_type: 'product',
        value: properties.value || properties.price,
        currency: properties.currency || 'NOK',
        num_items: properties.contents?.length || properties.quantity || 1,
      };
    case 'InitiateCheckout':
      return {
        value: properties.value,
        currency: properties.currency || 'NOK',
        num_items: properties.item_count,
      };
    case 'CompleteRegistration':
      return {
        content_name: 'Quiz',
        status: 'complete',
      };
    case 'Lead':
      return {
        content_name: 'Quiz',
      };
    default:
      return properties;
  }
}

/**
 * Track an event in PostHog (all events) and Meta Pixel (standard events only).
 * Non-mapped events are intentionally NOT sent to Meta to avoid noise.
 */
export function trackEvent(eventName, properties = {}) {
  // PostHog — all events
  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  }

  // Meta Pixel — only mapped standard events, with correct parameter format
  if (META_PIXEL_ID && window.fbq) {
    const metaEvent = META_EVENT_MAP[eventName];
    if (metaEvent) {
      const metaParams = getMetaParams(metaEvent, properties);
      window.fbq('track', metaEvent, metaParams);
    }
    // Unmapped events: PostHog only (no trackCustom noise in Meta)
  }

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Event: ${eventName}`, properties);
  }
}

/**
 * Flush PostHog event queue. Call before navigating away from the page
 * (e.g., redirecting to Shopify checkout) to ensure events aren't lost.
 * Returns a promise that resolves when the flush completes (or after timeout).
 */
export function flushAnalytics() {
  return new Promise((resolve) => {
    if (POSTHOG_KEY && posthog._flush) {
      posthog._flush(resolve);
      // Safety timeout in case flush callback never fires
      setTimeout(resolve, 500);
    } else {
      resolve();
    }
  });
}

/**
 * Fire Meta PageView on SPA route changes (consent-gated).
 * Called from App.js on location changes.
 */
export function trackMetaPageView() {
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * Identify a user (e.g., after email capture)
 */
export function identifyUser(userId, traits = {}) {
  if (POSTHOG_KEY) {
    posthog.identify(userId, traits);
  }
}

/**
 * Reset user identity (e.g., on logout)
 */
export function resetUser() {
  if (POSTHOG_KEY) {
    posthog.reset();
  }
}

// ============ QUIZ EVENTS ============

export function trackQuizIntroViewed() {
  trackEvent('quiz_intro_viewed', {
    page: '/quiz',
    funnel: 'quiz',
  });
}

export function trackQuizStarted() {
  trackEvent('quiz_started', {
    page: '/quiz',
    funnel: 'quiz',
    ...(_trafficSource || {}),
  });
}

export function trackQuizStep(stepNumber, stepName, answer = null) {
  trackEvent('quiz_step_completed', {
    step_number: stepNumber,
    step_name: stepName,
    answer: answer,
    funnel: 'quiz',
  });
}

export function trackQuizCompleted(answers) {
  trackEvent('quiz_completed', {
    ...answers,
    funnel: 'quiz',
  });
}

export function trackQuizAbandoned(lastStep, lastStepName) {
  trackEvent('quiz_abandoned', {
    last_step: lastStep,
    last_step_name: lastStepName,
    funnel: 'quiz',
  });
}

export function trackQuizCompletionTime(seconds, numberOfSteps) {
  trackEvent('quiz_completion_time', {
    seconds,
    number_of_steps: numberOfSteps,
    funnel: 'quiz',
  });
}

// ============ RESULTS EVENTS ============

export function trackResultsViewed(bundleName, totalPrice, productCount) {
  trackEvent('results_viewed', {
    bundle_name: bundleName,
    total_price: totalPrice,
    product_count: productCount,
    currency: 'NOK',
    funnel: 'purchase',
    ...(_trafficSource || {}),
  });
}

export function trackBundleAddedToCart(bundleName, totalPrice, products) {
  trackEvent('add_to_cart', {
    bundle_name: bundleName,
    value: totalPrice,
    currency: 'NOK',
    content_type: 'bundle',
    contents: products.map(p => ({
      id: p.handle,
      name: p.title,
      price: p.price,
    })),
    funnel: 'purchase',
  });
}

// Which CTA button was clicked (main card vs sticky mobile bar)
export function trackResultsCTAClicked(source, bundleName, totalPrice) {
  trackEvent('results_cta_clicked', {
    source, // 'main_card' | 'sticky_bar'
    bundle_name: bundleName,
    value: totalPrice,
    currency: 'NOK',
    funnel: 'purchase',
  });
}

// When user expands the individual products accordion
export function trackIndividualProductsExpanded(bundleName) {
  trackEvent('individual_products_expanded', {
    bundle_name: bundleName,
    funnel: 'purchase',
  });
}

// When user expands the profile accordion
export function trackProfileExpanded() {
  trackEvent('profile_expanded', {
    funnel: 'purchase',
  });
}

// When user clicks a product name link → /produkt/:handle
export function trackProductLinkClicked(productHandle, productTitle, rank) {
  trackEvent('product_link_clicked', {
    product_id: productHandle,
    product_name: productTitle,
    rank,
    funnel: 'purchase',
  });
}

// When user clicks a quick-question chip in the AI chat
export function trackQuickQuestionClicked(question) {
  trackEvent('quick_question_clicked', {
    question,
    funnel: 'purchase',
  });
}

// When AI recommendation finishes loading and renders on screen
export function trackRecommendationShown(bundleName, productCount, totalPrice, loadTimeMs, productHandles = []) {
  trackEvent('recommendation_shown', {
    bundle_name: bundleName,
    product_count: productCount,
    total_price: totalPrice,
    currency: 'NOK',
    load_time_ms: loadTimeMs,
    recommended_products: productHandles,
    funnel: 'purchase',
  });
}

// When user copies the LUME10 coupon code
export function trackCouponCopied() {
  trackEvent('coupon_copied', {
    coupon_code: 'LUME10',
    funnel: 'purchase',
  });
}

// Right before redirecting to Shopify checkout
export function trackCheckoutRedirect(checkoutUrl, value, itemCount) {
  trackEvent('checkout_redirect', {
    checkout_url: checkoutUrl ? new URL(checkoutUrl).hostname : '',
    value,
    currency: 'NOK',
    item_count: itemCount,
    funnel: 'purchase',
  });
}

// When countdown timer is shown to the user
export function trackTimerImpression(timerDurationRemainingSeconds) {
  trackEvent('timer_impression', {
    timer_duration_remaining_seconds: timerDurationRemainingSeconds,
    funnel: 'purchase',
  });
}

// How far user scrolls on results page (fires at 25%, 50%, 75%, 100%)
export function trackResultsScrollDepth(depthPercent) {
  trackEvent('results_scroll_depth', {
    depth_percent: depthPercent,
    funnel: 'purchase',
  });
}

// Single product added to cart from results page (not bundle)
export function trackSingleProductAddedToCart(productTitle, productHandle, price) {
  trackEvent('add_to_cart', {
    product_name: productTitle,
    product_id: productHandle,
    value: price,
    currency: 'NOK',
    content_type: 'single_product',
    funnel: 'purchase',
  });
}

// ============ PRODUCT EVENTS ============

export function trackProductViewed(product) {
  trackEvent('product_viewed', {
    product_id: product.handle,
    product_name: product.title,
    price: product.price,
    currency: 'NOK',
    content_type: 'product',
  });
}

export function trackProductAddedToCart(product, variant, quantity = 1) {
  trackEvent('add_to_cart', {
    product_id: product.handle,
    product_name: product.title,
    variant_id: variant?.id,
    variant_name: variant?.title,
    price: variant?.price || product.price,
    quantity: quantity,
    currency: 'NOK',
    content_type: 'product',
  });
}

// ============ CART EVENTS ============

export function trackCartViewed(productCount, totalValue) {
  trackEvent('cart_viewed', {
    product_count: productCount,
    value: totalValue,
    currency: 'NOK',
    funnel: 'purchase',
  });
}

// ============ CHECKOUT EVENTS ============

export function trackCheckoutStarted(cartValue, itemCount) {
  trackEvent('checkout_started', {
    value: cartValue,
    currency: 'NOK',
    item_count: itemCount,
    funnel: 'purchase',
    ...(_trafficSource || {}),
  });
}

export function trackPurchaseCompleted(orderId, totalValue, products) {
  trackEvent('purchase_completed', {
    order_id: orderId,
    value: totalValue,
    currency: 'NOK',
    content_type: 'product',
    contents: products,
    funnel: 'purchase',
  });
}

// ============ PAGE EVENTS ============

export function trackPageView(pageName, properties = {}) {
  trackEvent('page_viewed', {
    page_name: pageName,
    url: window.location.pathname,
    ...properties,
  });
}

export function trackHomepageViewed() {
  trackPageView('homepage');
}

export function trackStoreViewed() {
  trackPageView('store');
}

// ============ ENGAGEMENT EVENTS ============

export function trackReviewFormOpened(productHandle) {
  trackEvent('review_form_opened', {
    product_id: productHandle,
  });
}

export function trackReviewSubmitted(productHandle, rating) {
  trackEvent('review_submitted', {
    product_id: productHandle,
    rating: rating,
  });
}

export function trackAIChatOpened() {
  trackEvent('ai_chat_opened', {
    page: window.location.pathname,
  });
}

export function trackAIChatMessageSent() {
  trackEvent('ai_chat_message_sent', {
    page: window.location.pathname,
  });
}

export default {
  getConsent,
  setConsent,
  hasFullConsent,
  initAnalytics,
  initAnonymousAnalytics,
  upgradeToFullAnalytics,
  trackEvent,
  identifyUser,
  resetUser,
  // Traffic Source
  getTrafficSource,
  // Quiz
  trackQuizIntroViewed,
  trackQuizStarted,
  trackQuizStep,
  trackQuizCompleted,
  trackQuizAbandoned,
  trackQuizCompletionTime,
  // Results
  trackResultsViewed,
  trackBundleAddedToCart,
  trackResultsCTAClicked,
  trackIndividualProductsExpanded,
  trackProfileExpanded,
  trackProductLinkClicked,
  trackQuickQuestionClicked,
  trackSingleProductAddedToCart,
  trackRecommendationShown,
  trackCouponCopied,
  trackCheckoutRedirect,
  trackTimerImpression,
  trackResultsScrollDepth,
  // Products
  trackProductViewed,
  trackProductAddedToCart,
  // Cart
  trackCartViewed,
  // Checkout
  trackCheckoutStarted,
  trackPurchaseCompleted,
  // Pages
  trackPageView,
  trackHomepageViewed,
  trackStoreViewed,
  // Engagement
  trackReviewFormOpened,
  trackReviewSubmitted,
  trackAIChatOpened,
  trackAIChatMessageSent,
  // SPA PageView
  trackMetaPageView,
};
